'use client';

import { PrismicRichText } from '@prismicio/react';
import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import SplashButton from './SplashButton';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { ServiceProblem } from '@/types/service';
import { GiCrossMark } from 'react-icons/gi';
import { LuSearch, LuSearchCheck } from 'react-icons/lu';
import { asHTML, RichTextField } from '@prismicio/client';

interface ServiceIntroductionProps {
  text?: RichTextField;
  className?: string;
  lang?: string;
  title: RichTextField;
  subtitle?: string;
  button?: string;
  problems?: ServiceProblem[];
  footer?: RichTextField;
  imageSrc?: string;
}

const ServiceIntroduction: React.FC<ServiceIntroductionProps> = ({
  text,
  title,
  subtitle,
  button,
  className = '',
  lang,
  problems,
  footer,
  imageSrc: imageSrcProp,
}) => {
  const pathname = usePathname();
  // Extract the last segment as the slug
  const slug = pathname?.split('/').filter(Boolean).pop() || '';
  const isIntegrations = slug === 'integrations' || pathname?.includes('/integrations/');
  const [checkedSet, setCheckedSet] = useState<Set<number>>(new Set());
  const problemsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isIntegrations || !problems?.length) return;
    const el = problemsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        problems.forEach((_, i) => {
          setTimeout(() => setCheckedSet((prev) => new Set([...prev, i])), i * 250);
        });
        observer.disconnect();
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isIntegrations, problems]);

  let imageSrc = imageSrcProp || '/crash.jpg';
  if (!imageSrcProp) {
    if (slug === 'implementations') imageSrc = '/intro_implementations.jpg';
    else if (slug === 'integrations') imageSrc = '/intro_integrations.jpg';
  }

  return (
    <>
      <section
        id="intro"
        className={`to relative flex items-center justify-center overflow-hidden bg-white ${className}`}
      >
        <div className="relative">
          <div className="container grid py-12 sm:py-20 md:max-w-7xl">
            <div>
              {subtitle && (
                <p className="mb-6 text-sm font-medium uppercase text-gray-400">{subtitle}</p>
              )}
              <h2
                className="highlighter relative mb-6 max-w-3xl pr-2 text-left text-3xl font-normal leading-tight sm:text-left md:text-4xl  md:text-black"
                dangerouslySetInnerHTML={{ __html: asHTML(title) }}
              />
              <div className="service-intro blog-content max-w-3xl">
                <PrismicRichText field={text} />
              </div>

              <div className="my-10 grid grid-cols-1 items-center gap-8 md:grid-cols-2">
                <div ref={problemsRef}>
                  {problems?.map((problem, i) => (
                    <div key={i} className="mb-8 grid grid-cols-[1.5rem_1fr] items-start gap-6">
                      <div className="">
                        {isIntegrations ? (
                          <div className="relative mr-2 mt-1" style={{ fontSize: '2.5em', width: '1em', height: '1em' }}>
                            <LuSearch className="absolute inset-0 text-blue-800 transition-opacity duration-500" style={{ opacity: checkedSet.has(i) ? 0 : 1 }} />
                            <LuSearchCheck className="absolute inset-0 text-blue-800 transition-opacity duration-500" style={{ opacity: checkedSet.has(i) ? 1 : 0 }} />
                          </div>
                        ) : (
                          <GiCrossMark className="mr-2 mt-1 text-red-600" style={{ fontSize: '2.5em' }} />
                        )}
                      </div>
                      <div>
                        <h3 className={`mb-1 text-base font-black uppercase ${isIntegrations ? 'text-blue-800' : 'text-red-600'}`}>
                          {problem.header}
                        </h3>
                        <p className="text-base font-light">{problem.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex h-full w-full items-center justify-center">
                  <Image
                    src={imageSrc}
                    alt="Crash illustration"
                    width={400}
                    height={400}
                    className="h-auto max-w-full rounded-xl object-contain"
                    priority
                  />
                </div>
              </div>

              <div className="service-intro blog-content max-w-3xl">
                <PrismicRichText field={footer} />
              </div>

              <div className="flex items-center justify-start align-middle">
                <div className="mr-0 text-center sm:text-left">
                  {button && (
                    <SplashButton
                      text={button || ''}
                      url={`/${lang}/contact/`}
                      color="red"
                      size="lg"
                      className="mb-6 mt-6"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="hidden">
            <Image
              src="/oracle-netsuite-logo.png"
              alt="Netsuite ERP Award for OnePacific"
              quality={100}
              width={600}
              height={144}
              className="absolute left-[27%] top-24 h-auto w-[600px] -translate-x-[100%] grayscale fade-in"
            />
          </div>
        </div>
        <div className="absolute -top-10 bottom-0 left-0 z-10 hidden w-40 bg-gradient-to-r from-gray-100 to-transparent sm:w-96" />
      </section>
    </>
  );
};

export default ServiceIntroduction;
